import { Text, View } from "react-native";
import { useResidentContext } from "../../provider/ResidentProvider";
import { formatDate } from "ui/utils/dateUtils";
import { useMemo, useRef } from "react";
import KeyValueGrid from "ui/organisms/KeyValueGrid";
import { MediaModel } from "app/services/api/types/common/models/MediaModel";
import { EditResidentHistoryModal } from "../modals/EditResidentHistoryModal";
import { EditButton } from "../../molecules/EditButton";
import ViewDocuments from "app/components/molecules/ViewDocuments";
import { EditResidentVaccinationsModal } from "../modals/EditResidentVaccinationsModal";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { EditResidentOtherInfoModal } from "../modals/EditResidentOtherInfoModal";
import { useGetOtherInformationCategoriesQuery } from "app/services/api/categoriesApi";

export default function ResidentHistoryTemplate() {
  const { resident } = useResidentContext();

  // PERSONAL HISTORY
  const ref =
    useRef<React.ComponentRef<typeof EditResidentOtherInfoModal>>(null);

  const startEdit = () => {
    ref.current?.show(resident);
  };
  const { data: otherCategories } = useGetOtherInformationCategoriesQuery();

  const formattedItems = useMemo(() => {
    return (
      otherCategories?.map((category) => {
        const allInfo = resident?.other_information ?? [];

        const matchedInfo = allInfo.find(
          (info) =>
            info.other_information_category.id === category.id ||
            info.other_information_category.parent_id === category.id,
        );

        const matchedChild = category.children?.find((child) =>
          allInfo.some(
            (info) => info.other_information_category.id === child.id,
          ),
        );

        const value =
          matchedInfo?.description ??
          matchedChild?.name ??
          (matchedInfo ? "Yes" : "No");

        return {
          label: category.name,
          value,
        };
      }) || []
    );
  }, [resident?.other_information, otherCategories]);

  // MEDICAL HISTORY
  const refHistory =
    useRef<React.ComponentRef<typeof EditResidentHistoryModal>>(null);

  const startEditHistory = () => {
    refHistory.current?.show(resident);
  };

  const formattedConditions = useMemo(() => {
    if (!resident?.health_conditions) return [];

    return resident.health_conditions.map((condition) => ({
      label: condition.description,
      value: "",
    }));
  }, [resident?.health_conditions]);

  // VACCINATIONS
  const refVaccines =
    useRef<React.ComponentRef<typeof EditResidentVaccinationsModal>>(null);

  const startEditVaccination = () => {
    refVaccines.current?.show(resident);
  };

  const formattedVaccines = useMemo(() => {
    if (!resident?.vaccines) return [];

    return resident.vaccines
      .map((vaccine) => ({
        label: vaccine.name,
        value: formatDate(vaccine.date_of_administration),
        sortKey: new Date(vaccine.date_of_administration).getTime(),
      }))
      .sort((a, b) => b.sortKey - a.sortKey);
  }, [resident?.vaccines]);

  const healthConditionImages = useMemo(() => {
    if (!resident?.health_conditions) return [];

    return resident.health_conditions
      .map((condition) => condition.health_condition_media)
      .filter((media): media is MediaModel => Boolean(media));
  }, [resident?.health_conditions]);

  return (
    <View className="resident-container">
      <EditButton
        startEdit={startEdit}
        permission={PermissionsEnums.UPDATE_RESIDENT}
      />
      {resident?.other_information?.length ? (
        <KeyValueGrid items={formattedItems} />
      ) : (
        <Text className="text-typography-600">
          No other information listed.
        </Text>
      )}
      <View className="border border-gray-200 w-full my-12" />

      <EditButton
        startEdit={startEditHistory}
        permission={PermissionsEnums.UPDATE_RESIDENT}
      />
      {resident?.health_conditions?.length ? (
        <>
          <KeyValueGrid
            title="Health Conditions/Medical history"
            items={formattedConditions}
            isNumberList
          />
          {healthConditionImages.length > 0 ? (
            <ViewDocuments documents={healthConditionImages} className="mt-8" />
          ) : null}
        </>
      ) : (
        <Text className="text-typography-600">No medical history listed.</Text>
      )}
      <View className="border border-gray-200 w-full my-12" />

      <EditButton
        startEdit={startEditVaccination}
        permission={PermissionsEnums.UPDATE_RESIDENT}
      />

      {formattedVaccines.length > 0 ? (
        <KeyValueGrid
          title="Vaccinations"
          items={formattedVaccines}
          sortKey="sortKey"
          sortDescending={true}
          isNumberList
        />
      ) : (
        <Text className="text-typography-600">No vaccinations listed.</Text>
      )}
      <EditResidentOtherInfoModal ref={ref} />
      <EditResidentHistoryModal ref={refHistory} />
      <EditResidentVaccinationsModal ref={refVaccines} />
    </View>
  );
}
